import { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { DISCONNECT_USER_CONNECTED_APP } from '@/constants/graphql/mutations'
import { GET_USER_CONNECTED_APPS } from '@/constants/graphql/queries'
import { marketPresentation } from '@/config/marketPresentation'
import { cn } from '@/utils/cn'
import { convertToDate } from '@/utils/formatter'
import { Button, ModalBody, ModalFooter, ModalHeader, ModalTitle, useModal, useToast } from '@/components/base'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import ServerErrorScreen from '@/components/ServerErrorScreen'

type ConnectedApp = {
  id: string
  partnerName: string
  connectedAt?: string
  lastUsedAt?: string
}

export default function ConnectedAppsScreen() {
  const { t } = useTranslation()
  const toast = useToast()
  const { openModal, closeModal } = useModal()
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery(GET_USER_CONNECTED_APPS, {
    onError: (error) => {
      console.log(error)
    }
  })

  const [disconnectConnectedApp, { loading: disconnectLoading }] = useMutation(DISCONNECT_USER_CONNECTED_APP, {
    onCompleted: async () => {
      closeModal()
      setDisconnectingId(null)
      toast.show({ variant: 'success', description: t('profile:connectedAppsDisconnectSuccess') })
      await refetch()
    },
    onError: (error) => {
      closeModal()
      setDisconnectingId(null)
      console.log(error)
      toast.show({ variant: 'error', description: t('profile:connectedAppsDisconnectFailed') })
    }
  })

  const connectedApps = (data?.getUserConnectedApps ?? []) as ConnectedApp[]

  const onConfirmDisconnect = (id: string) => {
    setDisconnectingId(id)
    openModal({
      children: (
        <>
          <ModalHeader className={'p-4'}>
            <ModalTitle>{t('profile:connectedAppsDisconnectTitle')}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Text className={'text-sm text-text-secondary'}>
              {t('profile:connectedAppsDisconnectDesc', { token: marketPresentation.platformToken.displayName })}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button className={'flex-1'} variant={'outline'} size={'md'} onPress={closeModal}>
              {t('profile:cancel')}
            </Button>
            <Button
              className={'flex-1'}
              size={'md'}
              onPress={() => disconnectConnectedApp({ variables: { connectedAppId: id } })}
              disabled={disconnectLoading}
            >
              {t('profile:connectedAppsDisconnect')}
            </Button>
          </ModalFooter>
        </>
      ),
      onClose: () => {
        setDisconnectingId(null)
        closeModal()
      }
    })
  }

  if (loading) {
    return <LoadingScreen header={<NavigationBar hasBottomBorder={true}>{t('profile:connectedApps')}</NavigationBar>} />
  }

  if (!data) {
    return (
      <ServerErrorScreen
        onPress={refetch}
        header={<NavigationBar hasBottomBorder={true}>{t('profile:connectedApps')}</NavigationBar>}
      />
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar hasBottomBorder={true}>{t('profile:connectedApps')}</NavigationBar>
        }}
      />
      {connectedApps.length === 0 ? (
        <View className={'flex-1 items-center justify-center p-4'}>
          <Text className={'text-sm text-text-secondary'}>{t('profile:connectedAppsEmpty')}</Text>
        </View>
      ) : (
        <ScrollView className={'bg-background-secondary'}>
          <View className={'mx-4 mt-4 overflow-hidden rounded-xl border border-border-10 bg-background-primary'}>
            {connectedApps.map((connectedApp, index) => (
              <View
                key={connectedApp.id}
                className={cn('ml-4 border-border-10 py-3 pr-4', index !== connectedApps.length - 1 && 'border-b')}
              >
                <View className={'flex-row items-center justify-between'}>
                  <Text className={'text-base font-medium text-text-primary'}>{connectedApp.partnerName}</Text>
                  <Pressable
                    onPress={() => onConfirmDisconnect(connectedApp.id)}
                    disabled={disconnectLoading && disconnectingId === connectedApp.id}
                  >
                    {({ pressed }) => (
                      <Text className={cn('text-error text-sm', pressed && 'opacity-60')}>
                        {t('profile:connectedAppsDisconnect')}
                      </Text>
                    )}
                  </Pressable>
                </View>
                <Text className={'mt-2 text-xs text-text-tertiary'}>
                  {t('profile:connectedAppsDate', {
                    label: t('profile:connectedAppsConnectedAt'),
                    date: connectedApp.connectedAt ? convertToDate(new Date(connectedApp.connectedAt).valueOf()) : '-'
                  })}
                </Text>
                <Text className={'mt-1 text-xs text-text-tertiary'}>
                  {t('profile:connectedAppsDate', {
                    label: t('profile:connectedAppsLastUsedAt'),
                    date: connectedApp.lastUsedAt ? convertToDate(new Date(connectedApp.lastUsedAt).valueOf()) : '-'
                  })}
                </Text>
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </>
  )
}
